// TallerServicios.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  CssBaseline,
  TextField,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete, Add, RemoveCircleOutline, MoreVert } from '@mui/icons-material';
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import EntradaTallerForm from './EntradaTallerForm';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import AppTheme from '../../../shared-theme/AppTheme';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import SortableColumnWrapper from './SortableColumnWrapper';
import SortableTask from './SortableTask';
import { EmptyDropZone } from './EmptyDropZone';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const initialData = {
  porHacer: {
    title: 'POR HACER',
    tasks: [
      { id: 'KAN-5', title: 'Transferencias (movimiento entre almacenes)', user: 'M' },
      { id: 'KAN-6', title: 'Taller (servicio técnico o revisión de productos)', user: 'M' },
      { id: 'KAN-7', title: 'Productos (catálogo de productos o bienes financiables)', user: 'MA' },
    ],
  },
  enCurso: {
    title: 'EN CURSO',
    tasks: [
      { id: 'KAN-4', title: 'Almacenes (gestión de inventario por sucursal o bodega)', user: 'M' },
    ],
  },
  listo: {
    title: 'LISTO',
    tasks: [],
  },
};

const KanbanColumn = ({ columnKey, columnData, onTitleChange, onDeleteColumn, setOpenEntradaTaller, editingColumn, setEditingColumn, onMoveColumn }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#f9f9f9',
        borderRadius: 2,
        minHeight: '75vh',
        maxWidth: 300,
        minWidth: 300,
        display: 'flex',
        textAlign: 'start',
        flexDirection: 'column',
      }}
    >
      <Box display="flex" flexDirection="row" width="100%" alignItems="center" justifyContent="space-between">
        {editingColumn === columnKey ? (
          <TextField
            fullWidth
            value={columnData.title}
            onChange={(e) => onTitleChange(columnKey, e.target.value)}
            onBlur={() => setEditingColumn(null)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') setEditingColumn(null);
            }}
            size="small"
            autoFocus
          />
        ) : (
          <Typography
            variant="subtitle3"
            fontWeight={600}
            sx={{ mb: 2, cursor: 'pointer' }}
            onDoubleClick={() => setEditingColumn(columnKey)}
          >
            {columnData.title} 
          </Typography>
        )}
        {columnKey != "porHacer" &&(
        <MoreVert fontSize="small" sx={{mt:-2}} onClick={handleMenuClick}/>
        )}
        {columnKey != "porHacer" &&(
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={() => { onMoveColumn(columnKey, -1); handleClose(); }}>Mover a la izquierda</MenuItem>
          <MenuItem onClick={() => { onMoveColumn(columnKey, 1); handleClose(); }}>Mover a la derecha</MenuItem>
          <MenuItem onClick={() => { onDeleteColumn(columnKey); handleClose(); }}>Eliminar</MenuItem>
        </Menu>
        )}
      </Box>
      <Stack spacing={2} flex={1}>
        <SortableContext items={columnData.tasks.length > 0 ? columnData.tasks.map(t => t.id) : [`empty-${columnKey}`]} strategy={verticalListSortingStrategy}>
          {columnData.tasks.length === 0 ? (
            <EmptyDropZone id={`empty-zone-${columnKey}`} />
          ) : (
            columnData.tasks.map((task) => (
              <SortableTask key={task.id} id={task.id} task={task} />
            ))
          )}
        </SortableContext>
        {columnKey === 'porHacer' && (
          <Button fullWidth variant="contained" color='primarybutton' size="small" sx={{ mt: 2, fontWeight: 'bold' }} onClick={() => setOpenEntradaTaller(true)}>
            + Crear
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default function TallerServicios(props) {
  const [openEntradaTaller, setOpenEntradaTaller] = useState(false);
  const [kanbanData, setKanbanData] = useState(initialData);
  const [editingColumn, setEditingColumn] = useState(null);
  const [activeTask, setActiveTask] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const addNewColumn = () => {
    const newKey = uuidv4();
    setKanbanData((prev) => ({
      ...prev,
      [newKey]: {
        title: 'Nueva Columna',
        tasks: [],
      },
    }));
    setEditingColumn(newKey);
  };

  const deleteColumn = (key) => {
    setKanbanData((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const onMoveColumn = (columnKey, direction) => {
    const keys = Object.keys(kanbanData);
    const index = keys.indexOf(columnKey);
    const newIndex = index + direction;


    if (newIndex <= 0 || newIndex >= keys.length) return;

    const reorderedKeys = arrayMove(keys, index, newIndex);
    const newKanban = {};
    reorderedKeys.forEach((key) => {
      newKanban[key] = kanbanData[key];
    });
    setKanbanData(newKanban);
  };

  const findColumnKeyByTaskId = (taskId) => {
    return Object.keys(kanbanData).find(key => kanbanData[key].tasks.find(task => task.id === taskId));
  };

  const findColumnKeyByDropTarget = (targetId) => {
    if (!targetId) return null;
    if (!targetId.startsWith('empty-zone-')) return findColumnKeyByTaskId(targetId);
    return targetId.replace('empty-zone-', '');
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;

    if (Object.keys(kanbanData).includes(active.id) && Object.keys(kanbanData).includes(over.id)) {
      const oldIndex = Object.keys(kanbanData).indexOf(active.id);
      const newIndex = Object.keys(kanbanData).indexOf(over.id);
      const reorderedKeys = arrayMove(Object.keys(kanbanData), oldIndex, newIndex);
      const newKanban = {};
      reorderedKeys.forEach(key => {
        newKanban[key] = kanbanData[key];
      });
      setKanbanData(newKanban);
      return;
    }

    const sourceCol = findColumnKeyByTaskId(active.id);
    const destCol = findColumnKeyByDropTarget(over.id);
    if (!sourceCol || !destCol) return;

    if (sourceCol === destCol) {
      const tasks = [...kanbanData[sourceCol].tasks];
      const oldIndex = tasks.findIndex(task => task.id === active.id);
      const newIndex = tasks.findIndex(task => task.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(tasks, oldIndex, newIndex);
      setKanbanData(prev => ({
        ...prev,
        [sourceCol]: {
          ...prev[sourceCol],
          tasks: reordered,
        },
      }));
    } else {
      const sourceTasks = [...kanbanData[sourceCol].tasks];
      const destTasks = [...kanbanData[destCol].tasks];
      const movedTask = sourceTasks.find(task => task.id === active.id);
      if (!movedTask) return;
      setKanbanData(prev => ({
        ...prev,
        [sourceCol]: {
          ...prev[sourceCol],
          tasks: sourceTasks.filter(task => task.id !== active.id),
        },
        [destCol]: {
          ...prev[destCol],
          tasks: [movedTask, ...destTasks],
        },
      }));
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={(theme) => ({ flexGrow: 1, backgroundColor: theme.vars ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)` : alpha(theme.palette.background.default, 1), overflow: 'auto' })}>
          <Stack spacing={2} sx={{ alignItems: 'left', mx: 3, pb: 5, mt: { xs: 8, md: 0 } }}>
            <Header parent="Logística y Almacenes" child="Centro de Servicios" />
            <Box sx={{ px: 4, py: 3 }} textAlign="left">
              <Typography variant="h4" fontWeight={600} gutterBottom>
                Centro de Servicios
              </Typography>
              <Button variant="outlined" startIcon={<Add />} sx={{ mb: 2 }} onClick={addNewColumn}>
                Añadir Columna
              </Button>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={Object.keys(kanbanData)} strategy={horizontalListSortingStrategy}>
                  <Grid container spacing={2}>
                    {Object.entries(kanbanData).map(([key, column]) => (
                      <Grid item key={key}>
                        <KanbanColumn
                          columnKey={key}
                          columnData={column}
                          onTitleChange={(colKey, newTitle) => setKanbanData((prev) => ({
                            ...prev,
                            [colKey]: {
                              ...prev[colKey],
                              title: newTitle,
                            },
                          }))}
                          onDeleteColumn={deleteColumn}
                          onMoveColumn={onMoveColumn}
                          setOpenEntradaTaller={setOpenEntradaTaller}
                          editingColumn={editingColumn}
                          setEditingColumn={setEditingColumn}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </SortableContext>
                <DragOverlay>
                  {activeTask && (
                    <Card sx={{ p: 1 }} variant="outlined">
                      <CardContent>
                        <Typography>{activeTask}</Typography>
                      </CardContent>
                    </Card>
                  )}
                </DragOverlay>
              </DndContext>
            </Box>
            <EntradaTallerForm open={openEntradaTaller} onClose={() => setOpenEntradaTaller(false)} />
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
